import { jwtDecode } from "jwt-decode";

// Utility function to get random color
export const getRandomColor = () => {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

// Utility function for random color with opacity
export const getRandomColorWithOpacity = (
  opacity: number,
  baseColor?: string
) => {
  const color = baseColor || getRandomColor();
  return color + Math.floor(opacity * 255).toString(16);
};
export const capitalizeLocation = (str: string): string => {
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
export function formatLocation(input: string): string {
  const [locationPart] = input.split("-");
  const trimmed = locationPart.trim();
  const formatted = trimmed
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

  return formatted;
}

interface MarketRecord {
  key: number;
  location: string;
  y: string; // sales value as string
  date: string;
  adspend: string;
}

interface MarketSummary {
  location: string;
  totalSales: number;
  count: number;
}

export function summarizeMarkets(data: MarketRecord[]): MarketSummary[] {
  const summaryMap = new Map<string, { total: number; count: number }>();

  for (const record of data) {
    // Skip records with missing or invalid location
    if (!record || !record.location) {
      continue;
    }
    const market = record.location.trim();
    const sales = parseFloat(record.y) || 0;

    if (!summaryMap.has(market)) {
      summaryMap.set(market, { total: sales, count: 1 });
    } else {
      const existing = summaryMap.get(market)!;
      existing.total += sales;
      existing.count += 1;
    }
  }

  // Convert Map to array for easier display/use
  return Array.from(summaryMap, ([location, { total, count }]) => ({
    location,
    totalSales: parseFloat(total.toFixed(2)), // optional rounding
    count,
  }));
}

export const US_STATES_GEOJSON =
  "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

export const STATE_NAME_TO_POSTAL: Record<string, string> = {
  Alabama: "AL",
  Alaska: "AK",
  Arizona: "AZ",
  Arkansas: "AR",
  California: "CA",
  Colorado: "CO",
  Connecticut: "CT",
  Delaware: "DE",
  Florida: "FL",
  Georgia: "GA",
  Hawaii: "HI",
  Idaho: "ID",
  Illinois: "IL",
  Indiana: "IN",
  Iowa: "IA",
  Kansas: "KS",
  Kentucky: "KY",
  Louisiana: "LA",
  Maine: "ME",
  Maryland: "MD",
  Massachusetts: "MA",
  Michigan: "MI",
  Minnesota: "MN",
  Mississippi: "MS",
  Missouri: "MO",
  Montana: "MT",
  Nebraska: "NE",
  Nevada: "NV",
  "New Hampshire": "NH",
  "New Jersey": "NJ",
  "New Mexico": "NM",
  "New York": "NY",
  "North Carolina": "NC",
  "North Dakota": "ND",
  Ohio: "OH",
  Oklahoma: "OK",
  Oregon: "OR",
  Pennsylvania: "PA",
  "Rhode Island": "RI",
  "South Carolina": "SC",
  "South Dakota": "SD",
  Tennessee: "TN",
  Texas: "TX",
  Utah: "UT",
  Vermont: "VT",
  Virginia: "VA",
  Washington: "WA",
  "West Virginia": "WV",
  Wisconsin: "WI",
  Wyoming: "WY",
  "District of Columbia": "DC",
};

export const currency = (n: number) =>
  n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
interface JWTPayload {
  tenant_id?: string;
  tenantId?: string;
  [key: string]: any;
}

export const getTeanantNameFromToken = (): string => {
  try {
    const token = localStorage.getItem("authToken");
    if (!token) return "there";
    const decoded = jwtDecode<JWTPayload>(token);
    const raw = decoded.name || decoded.sub || "";
    if (!raw) return "There";
    // If it looks like an email use only the local part
    const name = raw.includes("@") ? raw.split("@")[0] : raw;
    return name
      .replace(/[._\-]+/g, " ")
      .split(" ")
      .filter(Boolean)
      .map((w: String) => w.length > 3 ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w.toUpperCase())
      .join(" ");
  } catch {
    return "There";
  }
};

export const getTenantFromToken = (): string | null => {
  try {
    const token = localStorage.getItem("authToken");
    if (!token) return null;

    const decoded = jwtDecode<JWTPayload>(token);
    const tenantId = decoded.tenant_id || decoded.tenantId;

    if (!tenantId) return null;
    return tenantId.toString().toLowerCase();
  } catch (error) {
    console.error("Error decoding JWT token:", error);
    return null;
  }
};

export const getUserFromToken = (): string | null => {
  try {
    const token = localStorage.getItem("authToken");
    if (!token) return null;

    const decoded = jwtDecode<JWTPayload>(token);
    console.log("decoded ::", decoded)
    const userId = decoded.user_id;

    if (!userId) return null;
    return userId.toString().toLowerCase();
  } catch (error) {
    console.error("Error decoding JWT token:", error);
    return null;
  }
};

// Backend issues a short-lived "mfa_challenge"-typed token (see
// adrsta-models AuthService.authenticate_user) in place of a real
// accessToken while a user is mid-login-MFA-verification. It should never
// be treated as a real session - this lets PrivateRoute reject it as
// defense-in-depth even though it should never end up in
// localStorage["authToken"] in the first place.
export const isMfaChallengeToken = (token: string): boolean => {
  try {
    const decoded = jwtDecode<JWTPayload>(token);
    return decoded.type === "mfa_challenge";
  } catch (error) {
    console.error("Error decoding JWT token:", error);
    return false;
  }
};

export async function logoToBase64(logoSrc: string): Promise<string> {
  const response = await fetch(logoSrc);
  const blob     = await response.blob();
  return new Promise((resolve, reject) => {
    const reader   = new FileReader();
    reader.onload  = () => {
      // Strip the "data:image/png;base64," prefix — backend only needs raw base64
      const result = reader.result as string;
      resolve(result.split(",")[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}